import React, {type ButtonHTMLAttributes, type ForwardedRef, forwardRef} from "react";

import {cn} from "@isudio/utils";

import {Button, Tooltip, TooltipContent, TooltipTrigger} from "../";
import {GradientButton} from "../button/GradientButton";

export interface FooterElement {
  name: string;
  type: "button" | "gradientButton";
  action?: "button" | "submit" | "reset";
  class?: "mode" | "action";
  mode?: "crop" | "saturation" | "brightness" | "contrast" | "hue";
  icon?: string;
  label?: string;
  tooltip?: string;
  style?: string;
  disabled?: boolean;
  onClick?: (e?: any) => any;
  onClickParameter?: any;
  onEnter?: (e?: any) => any;
  hidden?: boolean;
}

interface FooterProps {
  name?: string;
  className?: string;
  height?: string;
  elements: FooterElement[];
  gap?: string;
  size?: string;
  mode?: string;
  showBg?: boolean;
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
}

export const ActiveButton = forwardRef<HTMLButtonElement, ButtonProps>(
  ({
     className,
     active,
     disabled,
     children,
     ...props
   }, ref) => {
    return (
      <Button
        ref={ref}
        variant="vanilla"
        disabled={disabled || false}
        className={cn(className, (active && !disabled ? `active` : ''), (disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'))}
        {...props}
      >
        {children}
      </Button>
    );
  }
);

export const ToolbarBottom = forwardRef<HTMLDivElement, FooterProps>(
  ({
     name,
     className,
     elements,
     gap = "gap-2",
     size = "text-2xl",
     mode = "",
     showBg = true
   },
    ref: ForwardedRef<HTMLDivElement>
  ) => {
  const getElement = ((el: FooterElement, index: number) => {
    switch (el.type) {
      case "button":
        if (el.class === "mode") {
          const buttonEl = (
            <ActiveButton
              key={index}
              tabIndex={-1}
              disabled={el?.disabled || false}
              className={
              cn(
                `${size} ${el?.style}`,
                `${name}__${el.type}`,
                `${name}__${el.type}-${el.name}`,
                `${name}__${el.class}-button`,
                'cursor-pointer'
              )}
              active={el.mode === mode}
              onClick={() => (el?.onClick ? el?.onClick(el.mode) : undefined)}
            >
              <i
                className={`fa-duotone fa-light ${el.icon}`}/>
            </ActiveButton>
          );

          return (
            (el?.tooltip && el?.tooltip.length > 0) ? (
              <Tooltip delayDuration={0}>
                <TooltipTrigger
                  asChild
                  onFocus={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}>
                  {buttonEl}
                </TooltipTrigger>
                <TooltipContent side={"top"}>
                  <p>{el?.tooltip}</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              buttonEl
            )
          );

        } else {
          const buttonEl = (
            <Button
              key={index}
              variant="vanilla"
              tabIndex={-1}
              disabled={el.disabled}
              className={
              cn(
                `${name}__${el.type}`,
                `${name}__${el.type}-${el.name}`,
                `${name}__${el.class}-button`,
                `${size} ${el?.style}`,
                (el.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer')
              )}
              onClick={() => (el?.onClick ? el?.onClick(el?.onClickParameter) : undefined)}
            >
              <i
                className={`fa-duotone fa-light ${el.icon}`}/>
            </Button>
          );

          return (
            (el?.tooltip && el?.tooltip.length > 0) ? (
              <Tooltip delayDuration={0}>
                <TooltipTrigger
                  asChild
                  onFocus={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}>
                  {buttonEl}
                </TooltipTrigger>
                <TooltipContent side={"top"}>
                  <p>{el?.tooltip}</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              buttonEl
            )
          );
        }

      case "gradientButton":
        return (
          <GradientButton
            tabIndex={-1}
            type={el?.action}
            text={el?.label}
            className={
            cn(
              `${name}__${el.type}`,
              `${name}__${el.type}-${el.name}`,
              `${el?.style}`
            )}
            disabled={el.disabled}
            onClick={() => (el?.onClick ? el?.onClick(el?.name) : undefined)}
            onEnter={() => (el?.onEnter ? el?.onEnter() : undefined)}
          />
        );
    }
  });

  return (
    <div
      ref={ref}
      className={cn(
        `toolbar ${name} h-fit mt-0 p-2 flex items-center justify-center`,
        (showBg ? 'bg-linear-to-t from-gray-darker to-gray-medium' : ''),
        className
      )}
    >
      <div
        className={cn(`flex items-center justify-center ${gap}`, `${name}__buttons`)}>
        {elements.filter(el => !el.hidden).map((el, index) => (
          getElement(el, index)
        ))}
      </div>
    </div>
  );
});
